import { CheckoutStatus, ItemQuantity, ReservationStatus } from '../../../@types/graphql.d';
import {
	CheckoutConflictItem,
	getCheckoutItemConflict,
	getReservationItemConflict,
	ItemConflict,
	ReservationConflictItem,
} from './get-item-conflict';

export enum KitConflict {
	AVAILABLE = 'AVAILABLE',
	UNAVAILABLE = 'UNAVAILABLE',
	RESTRICTED = 'RESTRICTED',
	PARTIALLY_AVAILABLE = 'PARTIALLY_AVAILABLE',
	PARTIALLY_RESTRICTED = 'PARTIALLY_RESTRICTED',
	AT_WRONG_LOCATION = 'WRONG_LOCATION',
	CHECKED_OUT = 'CHECKED_OUT',
	ASSIGNED = 'IN_CUSTODY',
	CHECKING_OUT = 'CHECKING_OUT',
	FLAGGED = 'FLAGGED',
	RESERVED = 'RESERVED',
	CANNOT_CHECKOUT = 'CANNOT_CHECKOUT',
	CANNOT_RESERVE = 'CANNOT_RESERVE',
	RETIRED = 'EXPIRED',
	INCOMPLETE = 'INCOMPLETE',
	PARTIALLY_INCOMPLETE = 'PARTIALLY_INCOMPLETE',
	BUFFER_TIME = 'BUFFER_TIME',
}

type ConflictKit<TItem> = {
	id: string;
	items: TItem[];
	locked?: boolean;
	itemIds: string[];
	itemQuantities: ItemQuantity[];
};
export type CheckoutConflictKit = ConflictKit<CheckoutConflictItem>;
export type ReservationConflictKit = ConflictKit<ReservationConflictItem>;

type ReservationContext = {
	bookingLocationId: string | null;
	bookingStatus: ReservationStatus | null;
	isMaintenanceUser?: boolean;
	validLocationIds?: string[];
};

type CheckoutContext = {
	bookingId: string | null;
	bookingLocationId: string | null;
	bookingStatus: CheckoutStatus | null;
	isMaintenanceUser?: boolean;
	validLocationIds?: string[];
	isExtending?: boolean;
};

export const isKitComplete = (kit: CheckoutConflictKit | ReservationConflictKit) =>
	kit.itemQuantities.every(({ itemId, quantity }) => {
		// Picking explodes bulk items into seperate items,
		// which is visible on the item id as the item id is the parent item id and the bulk instance index
		// so we need to filter the items by the parent item id
		const items = kit.items.filter((it) => it.id.split('-')[0] === itemId);

		if (items.length === 0) return false;

		// Picking explodes bulk items into seperate items,
		// so we need to check if the quantity is equal to the number of items
		if (items.length > 1) {
			return quantity === items.length;
		}

		const item = items[0];
		return item.bookedQuantity != null ? item.bookedQuantity === quantity : true;
	});

export function getKitConflict(
	kit: ReservationConflictKit | CheckoutConflictKit,
	rule: 'reservation' | 'check-out',
	context: ReservationContext | CheckoutContext
): KitConflict;
export function getKitConflict<Rule extends 'reservation' | 'check-out'>(
	kit: Rule extends 'reservation' ? ReservationConflictKit : CheckoutConflictKit,
	rule: Rule,
	context: Rule extends 'reservation' ? ReservationContext : CheckoutContext
): KitConflict {
	switch (rule) {
		case 'check-out': {
			const currentKit = kit as CheckoutConflictKit;
			const currentContext = context as CheckoutContext;
			return getCheckoutKitConflict(currentKit, currentContext);
		}
		case 'reservation': {
			const currentKit = kit as ReservationConflictKit;
			const currentContext = context as ReservationContext;

			return getReservationKitConflict(currentKit, currentContext);
		}
		default:
			throw new Error(`Unknown rule: ${rule}`);
	}
}

function getKitConflictFromItemConflicts(itemConflicts: ItemConflict[]): KitConflict {
	const areAllItemsConflicted = itemConflicts.every((conflict) => conflict !== ItemConflict.AVAILABLE);
	if (areAllItemsConflicted) {
		const uniqueItemConflicts = new Set(itemConflicts);

		if (uniqueItemConflicts.size === 1) {
			switch (itemConflicts[0]) {
				case ItemConflict.RESTRICTED:
					return KitConflict.RESTRICTED;
				case ItemConflict.AT_WRONG_LOCATION:
					return KitConflict.AT_WRONG_LOCATION;
				case ItemConflict.CHECKED_OUT:
					return KitConflict.CHECKED_OUT;
				case ItemConflict.CHECKING_OUT:
					return KitConflict.CHECKING_OUT;
				case ItemConflict.ASSIGNED:
					return KitConflict.ASSIGNED;
				case ItemConflict.FLAGGED:
					return KitConflict.FLAGGED;
				case ItemConflict.RESERVED:
					return KitConflict.RESERVED;
				case ItemConflict.CANNOT_CHECKOUT:
					return KitConflict.CANNOT_CHECKOUT;
				case ItemConflict.CANNOT_RESERVE:
					return KitConflict.CANNOT_RESERVE;
				case ItemConflict.RETIRED:
					return KitConflict.RETIRED;
				case ItemConflict.INCOMPLETE_KIT:
					return KitConflict.INCOMPLETE;
				case ItemConflict.BUFFER_TIME:
					return KitConflict.BUFFER_TIME;
			}
		}

		return KitConflict.UNAVAILABLE;
	}

	const someItemsAreConflicted = itemConflicts.some((conflict) => conflict !== ItemConflict.AVAILABLE);
	if (someItemsAreConflicted) {
		if (itemConflicts.includes(ItemConflict.RESTRICTED)) {
			return KitConflict.PARTIALLY_RESTRICTED;
		}

		return KitConflict.PARTIALLY_AVAILABLE;
	}

	return KitConflict.AVAILABLE;
}

function getCheckoutKitConflict(kit: CheckoutConflictKit, context: CheckoutContext) {
	const itemConflicts = kit.items.map((item) => {
		return getCheckoutItemConflict(
			{
				...item,
				kitId: kit.id,
			},
			{
				checkoutLocation: context.bookingLocationId,
				checkoutStatus: context.bookingStatus,
				checkoutId: context.bookingId,
				isMaintenanceUser: context.isMaintenanceUser,
				isExtending: context.isExtending,
				validLocationIds: context.validLocationIds,
			}
		);
	});

	const kitConflictFromItems = getKitConflictFromItemConflicts(itemConflicts);

	if (!context.bookingStatus || context.bookingStatus === CheckoutStatus.DRAFT) {
		if (kit.locked) {
			if (!isKitComplete(kit)) {
				return KitConflict.INCOMPLETE;
			} else if (
				kitConflictFromItems === KitConflict.PARTIALLY_AVAILABLE ||
				kitConflictFromItems === KitConflict.PARTIALLY_RESTRICTED ||
				kitConflictFromItems === KitConflict.UNAVAILABLE
			) {
				return KitConflict.PARTIALLY_INCOMPLETE;
			}
		}
	}

	return kitConflictFromItems;
}

function getReservationKitConflict(kit: ReservationConflictKit, context: ReservationContext) {
	const itemConflicts = kit.items.map((item) => {
		return getReservationItemConflict(
			{
				...item,
				kitId: kit.id,
			},
			{
				reservationLocation: context.bookingLocationId,
				reservationStatus: context.bookingStatus,
				isMaintenanceUser: context.isMaintenanceUser,
				validLocationIds: context.validLocationIds,
			}
		);
	});

	const kitConflictFromItems = getKitConflictFromItemConflicts(itemConflicts);

	if (
		!context.bookingStatus ||
		context.bookingStatus === ReservationStatus.OPEN ||
		context.bookingStatus === ReservationStatus.DRAFT
	) {
		if (kit.locked) {
			if (!isKitComplete(kit)) {
				return KitConflict.INCOMPLETE;
			} else if (
				kitConflictFromItems === KitConflict.PARTIALLY_AVAILABLE ||
				kitConflictFromItems === KitConflict.PARTIALLY_RESTRICTED ||
				kitConflictFromItems === KitConflict.UNAVAILABLE
			) {
				return KitConflict.PARTIALLY_INCOMPLETE;
			}
		}
	}

	return kitConflictFromItems;
}
